import { createContext, useContext, useMemo } from 'react';
import { useUserContext } from './UserContext';

// Maps our stored preference keys to BCP-47 locale tags used by Intl APIs.
const DATE_LOCALE = { dmy: 'en-GB', mdy: 'en-US', ymd: 'sv-SE' };
const NUMBER_LOCALE = { eu: 'de-DE', us: 'en-US' };

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const { user } = useUserContext();

  const value = useMemo(() => {
    const datePref   = user?.date_format   || 'dmy';
    const numberPref = user?.number_locale || 'eu';
    const dateLocale   = DATE_LOCALE[datePref]   || 'en-GB';
    const numberLocale = NUMBER_LOCALE[numberPref] || 'de-DE';

    /**
     * Format a date value.
     * @param {Date|string|number} d
     * @param {'short'|'medium'|'long'} size  controls how much detail is shown
     */
    const formatDate = (d, size = 'medium') => {
      if (!d) return '';
      const date = d instanceof Date ? d : new Date(d);
      if (isNaN(date)) return '';
      const opts =
        size === 'short'  ? { day: '2-digit', month: '2-digit', year: 'numeric' } :
        size === 'long'   ? { day: 'numeric', month: 'long',    year: 'numeric' } :
                            { day: '2-digit', month: 'short',   year: 'numeric' };
      return date.toLocaleDateString(dateLocale, opts);
    };

    /**
     * Format a number value.
     * @param {number} n
     * @param {object} opts  passed through to Intl.NumberFormat
     */
    const formatNumber = (n, opts = {}) => {
      if (n == null || isNaN(n)) return '';
      return new Intl.NumberFormat(numberLocale, opts).format(n);
    };

    /** Relative time ("3 days ago") — locale-aware. */
    const formatRelative = (d) => {
      if (!d) return '';
      const date = d instanceof Date ? d : new Date(d);
      if (isNaN(date)) return '';
      const diffSec = Math.round((date - Date.now()) / 1000);
      const rtf = new Intl.RelativeTimeFormat(dateLocale, { numeric: 'auto' });
      const abs = Math.abs(diffSec);
      if (abs < 60)   return rtf.format(diffSec, 'second');
      if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
      if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
      if (abs < 2592000) return rtf.format(Math.round(diffSec / 86400), 'day');
      if (abs < 31536000) return rtf.format(Math.round(diffSec / 2592000), 'month');
      return rtf.format(Math.round(diffSec / 31536000), 'year');
    };

    return { formatDate, formatNumber, formatRelative, datePref, numberPref };
  }, [user?.date_format, user?.number_locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}
